<template>
  <page-template :is-loading="isLoading" :data-exists="commitsData !== null">
    <template #pageHeadline>
      {{ $t('publishChanges.pageHeadline') }}
    </template>
    <template #secondLevelHeaderLeft>
      {{
        $t('publishChanges.pageSecondaryHeadline', {
          version: selectedCommit,
          buckets: selectedBucketNames.length,
        })
      }}
    </template>
    <template #secondLevelHeaderRight>
      <rbz-form-button
        :label="$t('publishChanges.buttonLabel')"
        @button-click="publish()"
        :disabled="isLoading || selectedBucketNames.length === 0"
        :tooltip="
          selectedBucketNames.length === 0 ? $t('publishChanges.noSelectedBucketsTooltip') : ''
        "
        classes="publish-button"
        data-qa="publish-button"
      />
    </template>
    <template #pageContent>
      <div class="rbz-columns">
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <rbz-dropdown
              :items="mapBuckets"
              :value="selectedBucketNames"
              @update:value="selectedBucketNames = $event"
              type="multi-select"
              :label="$t('publishChanges.targetBuckets')"
              classes="buckets-selection"
              data-qa="buckets-selection"
            />
          </div>
        </div>
      </div>
      <div class="rbz-field">
        <label class="rbz-label">
          {{ $t('publishChanges.commit') }}
        </label>
        <rbz-table
          :columns="commitsColumns"
          :table-data="commitsData"
          :row-classes="
            (item) => {
              return item.version === selectedCommit ? 'row-checked' : '';
            }
          "
          :sticky-header="true"
          :default-sort-column-index="-1"
          :show-buttons-column="false"
          :show-top-navbar="false"
          data-qa="commits-table"
        >
        </rbz-table>
      </div>
    </template>
  </page-template>
</template>

<script lang="ts">
import _ from 'lodash';
import type { AxiosError } from 'axios';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import { defineComponent } from 'vue';
import { Branch, Commit, GenericObject } from '@/types';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import PageTemplate from '@/views/PageTemplate.vue';
import RbzFormButton from '@/components/RbzFormButton.vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import DateTimeUtils from 'ui-component-library/helpers/DateTimeUtils';
import RbzDropdown from '@/components/RbzDropdown.vue';
import { RbzTableColumnOptions } from 'ui-component-library/rbz/types';

const TOAST_DURATION_PERSISTENT = 999999 * 1000;
export default defineComponent({
  name: 'PublishChanges',
  components: {
    RbzDropdown,
    RbzFormButton,
    PageTemplate,
    RbzTable,
  },
  data() {
    return {
      commitsColumns: [
        {
          id: 'date',
          title: this.$t('publishChanges.date'),
          fieldNames: ['date'],
          displayFunction: (item: Commit) => {
            return item.date ? DateTimeUtils.isoToNowCuriefenseFormat(item.date) : '';
          },
          tooltipFunction: (item: Commit) => {
            return item.date ? DateTimeUtils.isoToNowFullCuriefenseFormat(item.date) : '';
          },
          isSortByOriginalValue: true,
          classes: 'width-200px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'version',
          title: this.$t('publishChanges.version'),
          fieldNames: ['version'],
          displayFunction: (item: Commit) => {
            return item.version?.substr(0, 7) || '';
          },
          tooltipFunction: (item: Commit) => {
            return item.version;
          },
          classes: 'width-100px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'message',
          title: this.$t('publishChanges.message'),
          fieldNames: ['message'],
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'author',
          title: this.$t('publishChanges.author'),
          fieldNames: ['author'],
          classes: 'width-150px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'email',
          title: this.$t('publishChanges.email'),
          fieldNames: ['email'],
          classes: 'width-200px',
          cellContentClasses: 'ellipsis',
        },
      ] as RbzTableColumnOptions[],
      commitsData: [] as Commit[],
      publishInfo: null as GenericObject,
      selectedCommit: null,
      selectedBucketNames: [],
      isLoading: true,
    };
  },
  watch: {
    selectedBranch: {
      handler: async function (val, oldVal) {
        if (
          (this.$route.name as string).includes('Publish Changes') &&
          val?.id &&
          val?.id !== oldVal?.id
        ) {
          this.isLoading = true;
          await this.branchesStore.loadBranches();
          await this.loadPublishInfo();
          this.loadBranchLogs();
          this.isLoading = false;
        }
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    mapBuckets(): [] {
      if (this.publishInfo?.buckets?.length) {
        return this.publishInfo.buckets.map((item: GenericObject) => [
          item.name,
          `${item.name} | ${item.url}`,
        ]);
      } else {
        return [];
      }
    },

    selectedBranch(): Branch {
      return this.branchesStore.selectedBranch;
    },
    ...mapStores(useBranchesStore, useUserStore),

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },
  },
  methods: {
    async loadPublishInfo() {
      const failureMessage = this.$t('publishChanges.loadFailureMessage');
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `db/system/publishinfo`,
        failureMessage,
      });
      if (response) {
        this.publishInfo = response?.data;
        this.setDefaultBuckets();
      }
    },

    loadBranchLogs() {
      const latestCommit = this.branchesStore.selectedBranch.logs?.shift();
      this.commitsData = [
        {
          ...latestCommit,
          id: latestCommit?.version,
        },
      ];
      this.selectedCommit = this.commitsData?.[0]?.version || null;
    },

    setDefaultBuckets() {
      this.selectedBucketNames = [];
      const bucketList = _.find(this.publishInfo?.['branch_buckets'], (list) => {
        return list.name === this.selectedBranch.id;
      });
      this.selectedBucketNames = _.cloneDeep(
        _.filter(bucketList?.buckets, (bucket) => {
          return _.find(this.publishInfo?.buckets, (publishInfoBucket) => {
            return publishInfoBucket.name === bucket;
          });
        })
      );
    },

    async publish() {
      this.isLoading = true;
      const branchId = this.selectedBranch.id;
      const commit = this.selectedCommit;
      const bucketsToPublish = this.publishInfo?.buckets.filter((item: GenericObject) =>
        this.selectedBucketNames.includes(item.name)
      );
      const successMessage = this.$t('publishChanges.saveSuccessMessage', {
        branch: branchId,
        version: commit,
      });
      const failureMessage = this.$t('publishChanges.saveFailureMessage', {
        branch: branchId,
        version: commit,
      });

      const getDetailsFromError = (error: AxiosError): string | undefined => {
        const data = error?.response?.data as GenericObject | undefined;
        const raw =
          data?.detail?.message ?? (typeof data?.detail === 'string' ? data.detail : undefined);
        if (typeof raw === 'string') return raw;
        if (raw != null) return JSON.stringify(raw);
        return undefined;
      };

      await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'PUT',
        url: `tools/publish/${branchId}`,
        data: bucketsToPublish,
        successMessage,
        failureMessage,
        displayServerFailureMessage: true,
        messageDuration: TOAST_DURATION_PERSISTENT,
        disableFailureToast: true,
        onFail: (error: AxiosError) => {
          const message = getDetailsFromError(error);
          Utils.toastErrorWithDetails(failureMessage, message, {
            duration: TOAST_DURATION_PERSISTENT,
          });
        },
      });
      this.isLoading = false;
    },
  },
});
</script>
